import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { CheckCircle, XCircle, Phone } from 'lucide-react';

const Safety = () => {
  const { themeColors } = useTheme();

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '20px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const sectionStyle = {
    marginBottom: '60px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px'
  };

  const cardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const redCardStyle = {
    ...cardStyle,
    borderLeft: `4px solid ${themeColors.error}`
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const listStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '2',
    paddingLeft: '20px',
    marginBottom: '16px'
  };

  const checklistItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px'
  };

  const prohibitedItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
    color: themeColors.textSecondary
  };

  const linkStyle = {
    color: themeColors.primary,
    textDecoration: 'none',
    fontWeight: '500'
  };

  const iconTextStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: themeColors.text,
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Safety First</h1>
          <p style={subtitleStyle}>
            Your family's safety is our top priority. Learn about our safety guidelines, product standards, and how we keep our community protected.
          </p>
        </div>

        {/* Our Safety Commitment */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Our Safety Commitment</h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              At BabyResell, we believe every child deserves safe, quality products. That's why we've built safety into every part of our platform — from listing guidelines to buyer protection. Here's how we work to keep your family safe.
            </p>
          </div>
        </div>

        {/* Product Safety Guidelines */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Product Safety Guidelines</h2>

          {/* Before You List */}
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <CheckCircle size={24} color={themeColors.success} />
              Before You List — Seller Checklist
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Inspect all items for damage, missing parts, or wear that could pose a safety risk
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Check the CPSC (Consumer Product Safety Commission) recall database at{' '}
                <a href="https://www.cpsc.gov" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  cpsc.gov
                </a>
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Verify that items with expiration dates (car seats, cribs) are still within their usable period
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Include honest photos showing any wear, stains, or defects
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Note the brand, model number, and manufacturing date when possible
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Clean and sanitize items before shipping or meeting for pickup
              </p>
            </div>
          </div>

          {/* Before You Buy */}
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <CheckCircle size={24} color={themeColors.success} />
              Before You Buy — Buyer Checklist
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Ask the seller for the brand, model, and approximate age of the item
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Search for recalls on{' '}
                <a href="https://www.cpsc.gov" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  cpsc.gov
                </a>{' '}
                using the item's model number
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                Request additional photos of any areas of concern
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                For car seats: ask about accident history and check the expiration date on the sticker
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                For cribs: ensure slat spacing is no more than 2-3/8 inches apart
              </p>
            </div>
            <div style={checklistItemStyle}>
              <CheckCircle size={20} color={themeColors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ ...paragraphStyle, marginBottom: 0 }}>
                For clothing: check for loose buttons, drawstrings, or small parts that could be choking hazards
              </p>
            </div>
          </div>
        </div>

        {/* Prohibited Items */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Prohibited Items</h2>
          <div style={redCardStyle}>
            <div style={iconTextStyle}>
              <XCircle size={24} color={themeColors.error} />
              The following items may NOT be sold on BabyResell:
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Recalled products — Any item that has been recalled by the CPSC or manufacturer</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Expired car seats — Car seats past their expiration date (usually 6-10 years from manufacture)</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Car seats involved in accidents — Even minor accidents can compromise structural integrity</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Drop-side cribs — Banned by the CPSC since 2011</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Crib bumper pads — Associated with suffocation risk; banned in many states</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Inclined sleepers over 10 degrees — Recalled product category due to suffocation risk</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Items with damaged safety features — Broken harnesses, missing straps, cracked helmets</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Counterfeit or knockoff products — Fake versions of name-brand safety items</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Homemade car seats or helmets — Must be commercially manufactured and tested</span>
            </div>
            <div style={prohibitedItemStyle}>
              <XCircle size={18} color={themeColors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Any item that poses a known suffocation, strangulation, or choking hazard</span>
            </div>
          </div>
        </div>

        {/* Safe Transaction Tips */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Safe Transaction Tips</h2>

          <div style={cardStyle}>
            <h3 style={{ color: themeColors.text, fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              For Online Transactions:
            </h3>
            <ul style={listStyle}>
              <li>Always use BabyResell's built-in payment system — never pay outside the platform</li>
              <li>Don't share personal banking information, Social Security numbers, or passwords</li>
              <li>Be wary of deals that seem too good to be true</li>
              <li>Keep all communication within the BabyResell messaging system</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h3 style={{ color: themeColors.text, fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
              For Local Pickups:
            </h3>
            <ul style={listStyle}>
              <li>Meet in public, well-lit locations (police stations, coffee shops, library parking lots)</li>
              <li>Bring a friend or family member when possible</li>
              <li>Inspect items thoroughly before completing the transaction</li>
              <li>Trust your instincts — if something feels off, walk away</li>
              <li>Let someone know where you're going and when you expect to be back</li>
            </ul>
          </div>
        </div>

        {/* Reporting Safety Concerns */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Reporting Safety Concerns</h2>
          <div style={cardStyle}>
            <div style={iconTextStyle}>
              <Phone size={24} color={themeColors.primary} />
              If you encounter an unsafe product, suspicious activity, or a safety concern on BabyResell, please report it immediately.
            </div>
            <ul style={listStyle}>
              <li>Report a listing: Click the "Report" button on any item page</li>
              <li>Report a user: Click "Report" on their profile</li>
              <li>
                Email us:{' '}
                <a href="mailto:safety@interverse.app" style={linkStyle}>
                  safety@interverse.app
                </a>
              </li>
              <li>
                CPSC reporting: Report unsafe products at{' '}
                <a href="https://www.saferproducts.gov" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  saferproducts.gov
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Helpful Safety Resources */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Helpful Safety Resources</h2>
          <div style={cardStyle}>
            <ul style={listStyle}>
              <li>
                <a href="https://www.cpsc.gov" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  Consumer Product Safety Commission (CPSC): cpsc.gov
                </a>
              </li>
              <li>
                <a href="https://www.cpsc.gov/Recalls" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  CPSC Recall List: cpsc.gov/Recalls
                </a>
              </li>
              <li>
                <a href="https://www.safekids.org" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  Safe Kids Worldwide: safekids.org
                </a>
              </li>
              <li>
                <a href="https://healthychildren.org/English/safety-prevention/on-the-go/Pages/Car-Safety-Seats-Information-for-Families.aspx" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  AAP Car Seat Guidelines: healthychildren.org
                </a>
              </li>
              <li>
                <a href="https://www.nhtsa.gov/equipment/car-seats-and-booster-seats" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  National Highway Traffic Safety Administration: nhtsa.gov
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Safety;
